/**
 * The inbox's "requests" section (PPL-429) — Carl's canvas, artboard
 * mr-inbox-requests.
 *
 * The prompt is shown once per request; this is where they live afterwards.
 * A row carries the handle, the fact that someone is asking, and how long
 * they have waited — never the message itself, which stays in the prompt and
 * the conversation.
 *
 * Tapping a row re-opens the prompt for that request rather than the
 * conversation: the decision comes before the thread.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageRequestCard } from './MessageRequestPrompt';
import { markPrompted } from '../hooks/useMessageRequests';
import type { MessageRequest } from '../types/social';
import '../styles/messageRequest.css';

/** "2h", "yesterday", "3d" — the canvas's row stamp. */
function shortSince(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days}d`;
}

function RowAvatar({ handle, src }: { handle: string; src?: string | null }) {
  const bare = handle.replace(/^@+/, '');
  let hue = 7;
  for (const ch of bare) hue = (hue * 31 + ch.charCodeAt(0)) % 360;
  if (src) return <img className="mr-avatar" src={src} alt="" style={{ width: 38, height: 38 }} />;
  return (
    <div
      className="mr-avatar"
      aria-hidden="true"
      style={{
        width: 38,
        height: 38,
        fontSize: 14,
        background: `linear-gradient(140deg, oklch(0.64 0.16 ${hue}), oklch(0.48 0.19 ${(hue + 62) % 360}))`,
      }}
    >
      {bare.charAt(0).toUpperCase()}
    </div>
  );
}

export default function MessageRequestSection({ requests }: { requests: MessageRequest[] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState<MessageRequest | null>(null);
  const [toast, setToast] = useState('');

  if (requests.length === 0 && !open && !toast) return null;

  function onAnswered(chatId: string, action: 'accept' | 'decline' | 'block') {
    const answered = requests.find((r) => r.chatId === chatId);
    markPrompted(chatId);
    setOpen(null);
    if (action === 'accept') {
      // With the recipient, or the thread opens headed "User", skips the block
      // check, and sends the first reply with an empty recipientId.
      navigate(`/messages/${chatId}`, {
        state: {
          recipientId: answered?.requester.id,
          recipientName: answered?.requester.tetherHandle || '',
        },
      });
      return;
    }
    setToast(action === 'decline' ? 'request hidden' : 'blocked');
    setTimeout(() => setToast(''), 4000);
  }

  return (
    <>
      {requests.length > 0 && (
        <>
          <div className="mr-section-head">
            <span className="mr-section-title">requests</span>
            <span className="mr-section-count">{requests.length}</span>
          </div>
          <div className="mr-request-list">
            {requests.map((r) => {
              const handle = r.requester.tetherHandle?.startsWith('@')
                ? r.requester.tetherHandle
                : `@${r.requester.tetherHandle || 'user'}`;
              return (
                <button key={r.chatId} className="mr-request-row" onClick={() => setOpen(r)}>
                  <RowAvatar handle={handle} src={r.requester.profileImageUrl} />
                  <span className="mr-request-body">
                    <span className="mr-request-handle">{handle}</span>
                    <span className="mr-request-line">wants to send you a message</span>
                  </span>
                  <span className="mr-request-when">{shortSince(r.requestedAt)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {open && (
        <div
          className="mr-scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(null);
          }}
        >
          {/* source="inbox": the same card, answered from the requests list. */}
          <MessageRequestCard
            request={open}
            more={0}
            source="inbox"
            onClose={() => setOpen(null)}
            onAnswered={onAnswered}
          />
        </div>
      )}
      {toast && <div className="mr-toast">{toast}</div>}
    </>
  );
}
